/** A run of a revision comparison: lines both versions share, or a place where they differ. */
export type DiffBlock = { kind: "same"; lines: string[] } | { kind: "change"; del: string[]; add: string[] };

/** One piece of a changed line: kept, removed, or inserted. */
export interface Segment {
  kind: "same" | "del" | "add";
  text: string;
}

/** The LCS table of two sequences, read back as an edit script in order. */
function editScript<T>(a: T[], b: T[]): Array<{ kind: "same" | "del" | "add"; item: T }> {
  const n = a.length;
  const m = b.length;
  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: Array<{ kind: "same" | "del" | "add"; item: T }> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", item: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ kind: "del", item: a[i++]! });
    } else {
      out.push({ kind: "add", item: b[j++]! });
    }
  }
  while (i < n) out.push({ kind: "del", item: a[i++]! });
  while (j < m) out.push({ kind: "add", item: b[j++]! });
  return out;
}

/** diffBlocks compares two texts line by line, grouping the result into shared runs and changes. */
export function diffBlocks(before: string, after: string): DiffBlock[] {
  const blocks: DiffBlock[] = [];
  for (const step of editScript(before.split("\n"), after.split("\n"))) {
    const last = blocks[blocks.length - 1];
    if (step.kind === "same") {
      if (last?.kind === "same") last.lines.push(step.item);
      else blocks.push({ kind: "same", lines: [step.item] });
    } else {
      let change = last?.kind === "change" ? last : undefined;
      if (!change) {
        change = { kind: "change", del: [], add: [] };
        blocks.push(change);
      }
      (step.kind === "del" ? change.del : change.add).push(step.item);
    }
  }
  return blocks;
}

/** diffWords compares two lines word by word; whitespace stays attached to the word before it. */
export function diffWords(before: string, after: string): Segment[] {
  const tokens = (s: string) => s.match(/\S+\s*|\s+/g) ?? [];
  const segments: Segment[] = [];
  for (const step of editScript(tokens(before), tokens(after))) {
    const last = segments[segments.length - 1];
    if (last?.kind === step.kind) last.text += step.item;
    else segments.push({ kind: step.kind, text: step.item });
  }
  return segments;
}

/**
 * Whether a removed and an added line are the same line edited, rather than one line replaced by another —
 * only an edit reads better as a word diff than as a removal followed by an addition.
 */
export function isEdit(before: string, after: string): boolean {
  if (!before.trim() || !after.trim()) return false;
  const kept = diffWords(before, after)
    .filter((s) => s.kind === "same")
    .reduce((n, s) => n + s.text.trim().length, 0);
  return kept / Math.max(before.trim().length, after.trim().length) >= 0.4;
}
